import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    Basemap,
    DegradationType,
    ELEMENTARY_FAMILIES,
    ElementaryFamilyKey,
    LAYER_AVAILABILITY,
    LAYER_DEFAULT_INDICATOR,
    LAYER_INDICATORS,
    LAYER_KEYS,
    LayerKey,
    NO_INDICATOR,
} from './indicators';
import type { EditableField, EditNotice } from './edit';
import { ListFilterField, NO_FILTERS, RangeFilterField, ReferenceFilters } from './filters';

export const moduleName = 'MAP_V2';

export type RightPanelMode = 'none' | 'single' | 'multi';

// The legend card cycles full -> mini -> hidden -> full with a single button.
export type LegendMode = 'full' | 'mini' | 'hidden';
const NEXT_LEGEND_MODE: Record<LegendMode, LegendMode> = { full: 'mini', mini: 'hidden', hidden: 'full' };

// Figures of the KPI bar, for the perimeter being looked at. A null value means there is nothing to
// measure and is displayed as a dash. They come from the state and project layers, so they stay unset
// until those phases (see docs/map-v2/plan.md).
export type Kpis = {
    analyzedMeters: number | null;
    priorityMeters: number | null;
    criticalMeters: number | null;
    budgetEuros: number | null;
};

type ElementaryFamilyState = {
    // Only used by families without children (e.g. "Pas élémentaires").
    on: boolean;
    kids: Record<string, boolean>;
};

export type MapV2State = {
    leftPanelCollapsed: boolean;
    fullscreen: boolean;
    // Only one layer is displayed at a time (radio behaviour in the maquette), or none.
    // A layer is "visible" exactly when it is the active one, so the visibility switch and the
    // "–" indicator can never get out of sync.
    activeLayer: LayerKey | null;
    // Accordion in the left panel: at most one expanded card.
    openLayer: LayerKey | null;
    // Indicator remembered for each layer (never "–"). Shown when the layer is hidden and restored
    // when it is switched back on.
    indicators: Record<LayerKey, string>;
    // "Appliquer les filtres de la vue tabulaire", one switch per layer.
    applyTableFilters: Record<LayerKey, boolean>;
    sessionsDegradationType: DegradationType;
    // Section "Données élémentaires" of the left panel: collapsible, with a master switch.
    elementaryOpen: boolean;
    elementaryEnabled: boolean;
    elementary: Record<ElementaryFamilyKey, ElementaryFamilyState>;
    // Families expanded to show their children (independent from each other).
    elementaryExpanded: Record<ElementaryFamilyKey, boolean>;
    basemap: Basemap;
    rightPanel: { open: boolean; mode: RightPanelMode };
    legendMode: LegendMode;
    kpis: Kpis | null;
    // Section the next Shift-click range starts from (the last one clicked or added).
    selectionAnchor: string | null;
    // Incremented when the map should bring the current selection into view (neighbor, extension).
    focusRequest: number;
    // Section under the pointer in the "tronçons retenus" list, highlighted on the map.
    listHoveredId: string | null;
    // Field being edited in the edit dialog (the selected sections are its scope), or null when closed.
    editDialog: EditableField | null;
    // Confirmation shown after a successful edit.
    notice: EditNotice | null;
    // Criteria of the référentiel filter panel.
    filters: ReferenceFilters;
};

const perLayer = <T>(value: (key: LayerKey) => T): Record<LayerKey, T> =>
    Object.fromEntries(LAYER_KEYS.map((key) => [key, value(key)])) as Record<LayerKey, T>;

// Everything is displayed by default (master switch, every family and every child on).
const initialElementary = (): Record<ElementaryFamilyKey, ElementaryFamilyState> =>
    Object.fromEntries(
        ELEMENTARY_FAMILIES.map((family) => [
            family.key,
            { on: true, kids: Object.fromEntries(family.children.map((child) => [child, true])) },
        ]),
    ) as Record<ElementaryFamilyKey, ElementaryFamilyState>;

const initialElementaryExpanded = (): Record<ElementaryFamilyKey, boolean> =>
    Object.fromEntries(ELEMENTARY_FAMILIES.map((family) => [family.key, false])) as Record<
        ElementaryFamilyKey,
        boolean
    >;

// The first layer wired to real data is displayed and expanded at start.
const firstAvailableLayer = (): LayerKey | null => LAYER_KEYS.find((key) => LAYER_AVAILABILITY[key]) ?? null;

const createInitialState = (): MapV2State => ({
    leftPanelCollapsed: false,
    fullscreen: false,
    activeLayer: firstAvailableLayer(),
    openLayer: firstAvailableLayer(),
    indicators: perLayer((key) => LAYER_DEFAULT_INDICATOR[key]),
    applyTableFilters: perLayer(() => false),
    sessionsDegradationType: 'Fissuration',
    elementaryOpen: true,
    elementaryEnabled: true,
    elementary: initialElementary(),
    elementaryExpanded: initialElementaryExpanded(),
    basemap: 'plan',
    rightPanel: { open: false, mode: 'none' },
    legendMode: 'full',
    kpis: null,
    selectionAnchor: null,
    focusRequest: 0,
    listHoveredId: null,
    editDialog: null,
    notice: null,
    filters: NO_FILTERS,
});

const initialState: MapV2State = createInitialState();

const mapV2Slice = createSlice({
    name: moduleName,
    initialState,
    reducers: {
        leftPanelToggled(state: MapV2State) {
            state.leftPanelCollapsed = !state.leftPanelCollapsed;
        },
        fullscreenSet(state: MapV2State, action: PayloadAction<boolean>) {
            state.fullscreen = action.payload;
        },
        basemapSet(state: MapV2State, action: PayloadAction<Basemap>) {
            state.basemap = action.payload;
        },

        // Layers
        // Visibility switch (and the radio strip of the collapsed panel). Showing a layer hides the
        // previous one; hiding the active layer leaves no layer displayed.
        layerVisibilitySet(state: MapV2State, action: PayloadAction<{ layer: LayerKey; visible: boolean }>) {
            const { layer, visible } = action.payload;
            if (!LAYER_AVAILABILITY[layer]) {
                return;
            }
            if (visible) {
                state.activeLayer = layer;
                state.openLayer = layer;
            } else {
                if (state.activeLayer === layer) {
                    state.activeLayer = null;
                }
                state.openLayer = null;
            }
        },
        // Click on a card header: expands or collapses it. Expanding a layer also displays it.
        layerExpandedToggled(state: MapV2State, action: PayloadAction<LayerKey>) {
            const layer = action.payload;
            if (!LAYER_AVAILABILITY[layer]) {
                return;
            }
            if (state.openLayer === layer) {
                state.openLayer = null;

                return;
            }
            state.activeLayer = layer;
            state.openLayer = layer;
        },
        // Choosing an indicator on a hidden layer only remembers it, it does not display the layer.
        // Choosing "–" on the active layer hides it.
        layerIndicatorSet(state: MapV2State, action: PayloadAction<{ layer: LayerKey; indicator: string }>) {
            const { layer, indicator } = action.payload;
            if (!LAYER_AVAILABILITY[layer] || !LAYER_INDICATORS[layer].includes(indicator)) {
                return;
            }
            if (indicator === NO_INDICATOR) {
                if (state.activeLayer === layer) {
                    state.activeLayer = null;
                }

                return;
            }
            state.indicators[layer] = indicator;
        },
        applyTableFiltersSet(state: MapV2State, action: PayloadAction<{ layer: LayerKey; value: boolean }>) {
            if (LAYER_AVAILABILITY[action.payload.layer]) {
                state.applyTableFilters[action.payload.layer] = action.payload.value;
            }
        },
        sessionsDegradationTypeSet(state: MapV2State, action: PayloadAction<DegradationType>) {
            state.sessionsDegradationType = action.payload;
        },
        layersReset(state: MapV2State) {
            const fresh = createInitialState();
            state.activeLayer = fresh.activeLayer;
            state.openLayer = fresh.openLayer;
            state.indicators = fresh.indicators;
            state.applyTableFilters = fresh.applyTableFilters;
            state.sessionsDegradationType = fresh.sessionsDegradationType;
        },

        // Elementary data
        elementarySectionToggled(state: MapV2State) {
            state.elementaryOpen = !state.elementaryOpen;
        },
        elementaryEnabledSet(state: MapV2State, action: PayloadAction<boolean>) {
            state.elementaryEnabled = action.payload;
        },
        // Only families that have children can be expanded.
        elementaryFamilyExpandedToggled(state: MapV2State, action: PayloadAction<ElementaryFamilyKey>) {
            if (Object.keys(state.elementary[action.payload].kids).length > 0) {
                state.elementaryExpanded[action.payload] = !state.elementaryExpanded[action.payload];
            }
        },
        elementaryFamilyToggled(state: MapV2State, action: PayloadAction<ElementaryFamilyKey>) {
            const family = state.elementary[action.payload];
            const childKeys = Object.keys(family.kids);
            if (childKeys.length === 0) {
                family.on = !family.on;

                return;
            }
            // Parent checkbox: if every child is on, switch all off; otherwise switch all on.
            const allOn = childKeys.every((child) => family.kids[child]);
            childKeys.forEach((child) => {
                family.kids[child] = !allOn;
            });
        },
        elementaryChildToggled(
            state: MapV2State,
            action: PayloadAction<{ family: ElementaryFamilyKey; child: string }>,
        ) {
            const { family, child } = action.payload;
            const { kids } = state.elementary[family];
            if (child in kids) {
                kids[child] = !kids[child];
            }
        },
        elementaryAllSet(state: MapV2State, action: PayloadAction<boolean>) {
            (Object.keys(state.elementary) as ElementaryFamilyKey[]).forEach((key) => {
                const family = state.elementary[key];
                const childKeys = Object.keys(family.kids);
                if (childKeys.length === 0) {
                    family.on = action.payload;
                }
                childKeys.forEach((child) => {
                    family.kids[child] = action.payload;
                });
            });
        },

        // Right panel
        rightPanelOpened(state: MapV2State, action: PayloadAction<Exclude<RightPanelMode, 'none'>>) {
            state.rightPanel = { open: true, mode: action.payload };
        },
        rightPanelClosed(state: MapV2State) {
            state.rightPanel = { open: false, mode: 'none' };
        },

        legendModeCycled(state: MapV2State) {
            state.legendMode = NEXT_LEGEND_MODE[state.legendMode];
        },
        kpisLoaded(state: MapV2State, action: PayloadAction<Kpis | null>) {
            state.kpis = action.payload;
        },

        selectionAnchorSet(state: MapV2State, action: PayloadAction<string | null>) {
            state.selectionAnchor = action.payload;
        },
        selectionFocusRequested(state: MapV2State) {
            state.focusRequest += 1;
        },
        listHoveredIdSet(state: MapV2State, action: PayloadAction<string | null>) {
            state.listHoveredId = action.payload;
        },

        editDialogOpened(state: MapV2State, action: PayloadAction<EditableField>) {
            state.editDialog = action.payload;
        },
        editDialogClosed(state: MapV2State) {
            state.editDialog = null;
        },
        noticeSet(state: MapV2State, action: PayloadAction<EditNotice | null>) {
            state.notice = action.payload;
        },

        filterListSet(state: MapV2State, action: PayloadAction<{ field: ListFilterField; values: string[] }>) {
            const { field, values } = action.payload;
            const lists = { ...state.filters.lists };
            if (values.length === 0) {
                delete lists[field];
            } else {
                lists[field] = values;
            }
            state.filters = { ...state.filters, lists };
        },
        filterRangeSet(
            state: MapV2State,
            action: PayloadAction<{ field: RangeFilterField; min: number | null; max: number | null }>,
        ) {
            const { field, min, max } = action.payload;
            const ranges = { ...state.filters.ranges };
            if (min === null && max === null) {
                delete ranges[field];
            } else {
                ranges[field] = { min, max };
            }
            state.filters = { ...state.filters, ranges };
        },
        filtersReset(state: MapV2State) {
            state.filters = NO_FILTERS;
        },

        // Esc peels one thing at a time: fullscreen first, then the right panel.
        escapePressed(state: MapV2State) {
            if (state.fullscreen) {
                state.fullscreen = false;
            } else if (state.rightPanel.open) {
                state.rightPanel = { open: false, mode: 'none' };
            }
        },
    },
});

export const {
    leftPanelToggled,
    fullscreenSet,
    basemapSet,
    layerVisibilitySet,
    layerExpandedToggled,
    layerIndicatorSet,
    applyTableFiltersSet,
    sessionsDegradationTypeSet,
    layersReset,
    elementarySectionToggled,
    elementaryEnabledSet,
    elementaryFamilyExpandedToggled,
    elementaryFamilyToggled,
    elementaryChildToggled,
    elementaryAllSet,
    rightPanelOpened,
    rightPanelClosed,
    legendModeCycled,
    kpisLoaded,
    selectionAnchorSet,
    selectionFocusRequested,
    listHoveredIdSet,
    editDialogOpened,
    editDialogClosed,
    noticeSet,
    filterListSet,
    filterRangeSet,
    filtersReset,
    escapePressed,
} = mapV2Slice.actions;
export const mapV2Reducer = mapV2Slice.reducer;
